import { Box, Button, Checkbox, Chip, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { formatDateInZone } from '../../../lib/tripTime';
import { plural } from '../../../lib/plural';
import type { GroupRoomRequest } from '../../../types/groups';

const REQUEST_STATUSES = ['new', 'contacted', 'held', 'invoiced', 'paid', 'booked', 'released', 'declined'];
const INVENTORY_REDUCING = new Set(['held', 'invoiced', 'paid', 'booked']);

export default function ReservationRow({
  request,
  timeZone,
  blockLabel,
  selected,
  showSelect,
  onToggleSelect,
  onStatusChange,
  onEditBooking,
  onManageOccupants,
  onInvoice,
  onSendConfirmation,
  sendingConfirmation,
}: {
  request: GroupRoomRequest;
  /** The trip's zone: a confirmation date is read in it, not in whatever zone the host's browser is in. */
  timeZone: string;
  blockLabel: string;
  selected: boolean;
  showSelect: boolean;
  onToggleSelect: () => void;
  onStatusChange: (status: string) => void;
  onEditBooking: () => void;
  onManageOccupants: () => void;
  onInvoice: () => void;
  onSendConfirmation: () => void;
  sendingConfirmation: boolean;
}) {
  // A membership or rate change after the confirmation went out leaves the guest
  // holding an email that is now wrong, and the API says so with resendNeeded.
  // Without this the chip still reads "Confirmed 9 Aug" beside a roommate who was
  // added afterwards and told nothing.
  const staleConfirmation = Boolean(request.confirmationSentAt && request.resendNeeded);
  const confirmationLabel = !request.confirmationSentAt
    ? 'Not confirmed'
    : staleConfirmation
      ? 'Confirmation out of date'
      : `Confirmed ${formatDateInZone(request.confirmationSentAt, timeZone)}`;
  return (
    <Paper sx={{ p: 2.5, borderRadius: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          {showSelect && (
            <Checkbox
              size="small"
              sx={{ mt: -0.5, ml: -1 }}
              checked={selected}
              onChange={onToggleSelect}
              inputProps={{ 'aria-label': `Select ${request.guestName}` }}
            />
          )}
          <Box>
            <Typography fontWeight={800}>{request.guestName}</Typography>
            <Typography variant="body2" color="text.secondary">{blockLabel}</Typography>
            <Typography variant="body2" color="text.secondary">{request.email || request.phone} · party {request.partySize} · {plural(request.quantity, 'room')}</Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {request.status === 'booked' && (
            <Chip
              size="small"
              variant="outlined"
              color={!request.confirmationSentAt ? 'default' : staleConfirmation ? 'warning' : 'success'}
              label={confirmationLabel}
            />
          )}
          <Chip label={request.status} color={INVENTORY_REDUCING.has(request.status) ? 'warning' : 'default'} />
        </Stack>
      </Box>
      {request.notes ? <Typography sx={{ mt: 1 }}>{request.notes}</Typography> : null}
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Status" value={request.status} onChange={(e) => onStatusChange(e.target.value)} sx={{ minWidth: 160 }}>
          {REQUEST_STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
        </TextField>
        {/*
          Unconditional, and it has to stay that way. The booking dialog — the
          only place the structured, guest-visible rate can be entered — used to
          open solely from the select beside this button, and MUI drops an
          onChange that re-picks the value already showing. Every reservation a
          host assigned or imported is born 'booked', so for those the dialog had
          no door at all: a whole trip's pricing went into the legacy per-night
          field the API never quotes to a guest. Re-gating this on status closes
          it again.
        */}
        <Button variant="outlined" onClick={onEditBooking}>Edit booking</Button>
        {/*
          Unconditional too, and for a plainer reason than the one above: who is
          sleeping in a room is a fact about the room, not about how far along the
          booking is. A host fills the beds before the hotel confirms anything.
        */}
        <Button variant="outlined" onClick={onManageOccupants}>Who’s in this room</Button>
        {request.status === 'booked' && (
          <Button
            variant="outlined"
            disabled={sendingConfirmation}
            onClick={() => {
              if (request.confirmationSentAt && !window.confirm(`Already confirmed on ${formatDateInZone(request.confirmationSentAt, timeZone)}. Resend?`)) return;
              onSendConfirmation();
            }}
          >
            {request.confirmationSentAt ? 'Resend confirmation' : 'Send confirmation'}
          </Button>
        )}
        <Button variant="outlined" onClick={onInvoice}>Mark invoice sent</Button>
      </Box>
    </Paper>
  );
}
